import { useState, useEffect, useMemo, useRef } from "react";
import { doc, getDoc, query, collection, where, getDocs, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions, auth } from "../firebase";

import { useSearchParams } from "react-router-dom";
import { signInAnonymously } from "firebase/auth";

interface PaymentState {
    userEmail: string;
    premiumAmount: number;
    selectedPackageId: string | null;
    errorMessage: string;
    errorStatus: boolean;
    success: boolean;
    paymentStatus: string;
    liveMode: boolean;
    paymentUrl?: string | null;
    paymentReference?: string | null;
    currentNextDueDate?: any;
    isExtension?: boolean;
    expiryDate?: Date | null;
}

export const usePayment = () => {
    const [searchParams] = useSearchParams();
    const [paymentState, setPaymentState] = useState<PaymentState>({
        userEmail: "",
        premiumAmount: 0,
        selectedPackageId: null,
        errorStatus: true,
        errorMessage: "",
        success: false,
        paymentStatus: "pending",
        liveMode: true,
        paymentUrl: null,
        paymentReference: null,
        currentNextDueDate: null,
        isExtension: false,
        expiryDate: null,
    });
    // Store the email used for payment to ensure it's available in callbacks
    const emailUsedForPayment = useRef<string>("");

    // Sign in anonymously when component mounts
    useEffect(() => {
        const signIn = async () => {
            try {
                await signInAnonymously(auth);
            } catch (error) {
                console.error("Error signing in anonymously:", error);
                setPaymentState((prev) => ({
                    ...prev,
                    errorStatus: true,
                    errorMessage: "Failed to initialize payment system. Please try again.",
                }));
            }
        };
        signIn();
    }, []);

    // Listen for payment confirmation via Firestore real-time updates on the user document
    useEffect(() => {
        let unsubscribe: (() => void) | null = null;

        if (paymentState.paymentStatus === "awaiting_payment" && paymentState.userEmail) {
            const q = query(
                collection(db, "users"),
                where("email", "==", paymentState.userEmail)
            );
            
            unsubscribe = onSnapshot(q, (snapshot) => {
                if (!snapshot.empty) {
                    const userData = snapshot.docs[0].data();
                    const newNextDueDate = userData.nextDueDate;
                    const prevNextDueDate = paymentState.currentNextDueDate;

                    // Check if nextDueDate has changed or is new
                    let isUpdated = false;
                    
                    if (!prevNextDueDate && newNextDueDate) {
                        isUpdated = true;
                    } else if (prevNextDueDate && newNextDueDate) {
                        // Compare timestamps
                        const prevMillis = prevNextDueDate.toMillis ? prevNextDueDate.toMillis() : new Date(prevNextDueDate).getTime();
                        const newMillis = newNextDueDate.toMillis ? newNextDueDate.toMillis() : new Date(newNextDueDate).getTime();
                        
                        if (newMillis > prevMillis) {
                            isUpdated = true;
                        }
                    }

                    if (isUpdated) {
                        const isExtension = prevNextDueDate ? true : false;
                        const expiryDate = newNextDueDate.toDate ? newNextDueDate.toDate() : new Date(newNextDueDate);
                        
                        setPaymentState((prev) => ({
                            ...prev,
                            paymentStatus: "success",
                            isExtension: isExtension,
                            expiryDate: expiryDate,
                        }));
                    }
                }
            });
        }

        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [paymentState.paymentStatus, paymentState.userEmail, paymentState.currentNextDueDate]);

    // Fetch live mode from Firestore (premium amount is now set by package selection)
    useEffect(() => {
        const fetchLiveMode = async () => {
            try {
                const setupDocRef = doc(db, "setup", "premium_settings");
                const setupDoc = await getDoc(setupDocRef);

                if (setupDoc.exists()) {
                    const data = setupDoc.data();
                    const liveMode = data.live_mode || false;

                    setPaymentState((prev) => ({
                        ...prev,
                        liveMode,
                    }));
                }
            } catch (error) {
                console.error("Error fetching live mode:", error);
            }
        };

        fetchLiveMode();
    }, []);

    // Fetch email from URL params
    useEffect(() => {
        const userEmail = searchParams.get("email") || "";

        setPaymentState((prev) => ({
            ...prev,
            userEmail,
        }));
    }, [searchParams]);

    // Generate a unique reference for this payment
    const generateRef = (): string => {
        const randomCode = Math.floor(Math.random() * 923417);
        return `${randomCode}`;
    };

    // Set selected package and fetch amount from Firestore
    const setSelectedPackage = async (packageId: string) => {
        try {
            const setupDocRef = doc(db, "setup", "premium_settings");
            const setupDoc = await getDoc(setupDocRef);

            if (!setupDoc.exists()) {
                setPaymentState((prev) => ({
                    ...prev,
                    errorStatus: true,
                    errorMessage: "Premium settings not found. Please contact support.",
                }));
                return;
            }

            const data = setupDoc.data();
            let premiumAmount = 0;

            // Map package IDs to Firestore fields
            switch (packageId) {
                case "1year":
                    premiumAmount = data.premium_basic || 20;
                    break;
                case "2year":
                    premiumAmount = data.premium_medium || 35;
                    break;
                case "4year":
                    premiumAmount = data.premium_full || 60;
                    break;
                default:
                    setPaymentState((prev) => ({
                        ...prev,
                        errorStatus: true,
                        errorMessage: "Invalid package selected.",
                    }));
                    return;
            }

            setPaymentState((prev) => ({
                ...prev,
                premiumAmount,
                selectedPackageId: packageId,
                errorMessage: "",
                errorStatus: false,
            }));
        } catch (error) {
            console.error("Error fetching package amount:", error);
            setPaymentState((prev) => ({
                ...prev,
                errorStatus: true,
                errorMessage: "Failed to fetch package amount. Please try again.",
            }));
        }
    };

    // Initialize Paystack payment config (memoized to recreate when amount or email changes)
    // Note: This will be recreated with the correct email when processPayment is called
    // Removed paymentConfig and initializePayment as we use TamarSoft redirect

    // Validate email format
    const isValidEmail = (email: string): boolean => {
        const emailRegex = /^[\w-]+@([\w-]+\.)+[\w-]{2,4}$/;
        return emailRegex.test(email);
    };

    const processPayment = async (emailOverride?: string): Promise<{ success: String }> => {
        setPaymentState((prev) => ({ ...prev, errorMessage: "", errorStatus: false }));

        // Use emailOverride if provided, otherwise use paymentState.userEmail
        const emailToUse = emailOverride || paymentState.userEmail;

        if (!emailToUse || !paymentState.premiumAmount) {
            setPaymentState((prev) => ({
                ...prev,
                errorMessage: emailToUse
                    ? "Please select a package"
                    : "Email is required to continue",
                errorStatus: true,
            }));
            return { success: "failed" };
        }

        if (!isValidEmail(emailToUse)) {
            setPaymentState((prev) => ({
                ...prev,
                errorMessage: "Sorry, email address is not valid",
                errorStatus: true,
            }));
            return { success: "failed" };
        }

        // Store the email that will be used for payment
        emailUsedForPayment.current = emailToUse;

        // Update paymentState with email if using override
        if (emailOverride && emailOverride !== paymentState.userEmail) {
            setPaymentState((prev) => ({
                ...prev,
                userEmail: emailOverride,
            }));
            // Wait for state update
            await new Promise((resolve) => setTimeout(resolve, 100));
        }

        try {
            // Check if email exists using the email to use
            const docRef = query(collection(db, "users"), where("email", "==", emailToUse));
            const docSnap = await getDocs(docRef);
            if (docSnap.empty) {
                setPaymentState((prev) => ({
                    ...prev,
                    errorMessage: "Sorry, This email is not registered. Kindly contact Support",
                    errorStatus: true,
                }));
                return { success: "failed" };
            }

            const userData = docSnap.docs[0].data();

            // Set payment status to processing before calling function
            setPaymentState((prev) => ({
                ...prev,
                paymentStatus: "processing",
            }));

            // Call TamarSoft payment function
            const createPayment = httpsCallable(functions, "createTamarSoftPayment");
            const result = await createPayment({
                email: emailToUse,
                amount: paymentState.premiumAmount,
                metadata: {
                    source: "web_app",
                },
            });

            const response = result.data as { url: string; reference: string };
            if (response.url) {
                // Set status to url_ready and store url
                setPaymentState((prev) => ({
                    ...prev,
                    paymentStatus: "url_ready",
                    paymentUrl: response.url,
                    paymentReference: response.reference,
                    currentNextDueDate: userData.nextDueDate,
                }));
                return { success: "pending" };
            } else {
                throw new Error("Failed to get payment URL");
            }
        } catch (error: any) {
            console.error(error);
            setPaymentState((prev) => ({
                ...prev,
                errorStatus: true,
                errorMessage: error.message || "Unable to complete payment. Kindly contact support",
                paymentStatus: "pending",
            }));
            return { success: "failed" };
        }
    };

    const redirectToCheckout = () => {
        if (paymentState.paymentUrl) {
            setPaymentState((prev) => ({
                ...prev,
                paymentStatus: "awaiting_payment",
            }));
            window.open(paymentState.paymentUrl, "_blank");
        }
    };

    // Set user email manually (for when email is not in URL)
    const setUserEmail = (email: string) => {
        setPaymentState((prev) => ({
            ...prev,
            userEmail: email,
            errorMessage: "",
            errorStatus: false,
        }));
    };

    return {
        paymentState,
        processPayment,
        setSelectedPackage,
        setUserEmail,
        redirectToCheckout,
    };
};
